import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, input, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, takeUntil } from 'rxjs';

import { Loggable } from 'app/utils/logging/loggable';
import { RingColor } from 'messages/websocket.messages';

import { socialAppSelectors } from '../state/app';

export interface RingColorContainerPosition {
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
  position?: 'fixed' | 'absolute' | 'sticky';
}

@Component({
  selector: 'app-ring-colors-legend',
  templateUrl: './ring-colors-legend.component.html',
  styleUrls: ['./ring-colors-legend.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RingColorsLegendComponent extends Loggable implements OnInit, OnDestroy {
  private store = inject(Store);

  protected readonly ringColorsList = signal<RingColor[]>([]);
  protected readonly legendName = signal<string | undefined>(undefined);
  protected showLegendComponent = false;
  protected dynamicallyPositionStyle?: RingColorContainerPosition;
  public readonly dynamicallyPosition = input<RingColorContainerPosition>();
  public readonly ringContainerPosition = input<RingColorContainerPosition>({
    top: '',
    left: '',
    right: '',
    bottom: '',
    position: 'absolute',
  });

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor() {
    super();
    this.setDisplayName('RingColorsLegendComponent');
  }

  public ngOnInit(): void {
    this.store
      .select(socialAppSelectors.getVisualIndicatorInfo)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((visualIndicatorInfo) => {
        this.ringColorsList.set(visualIndicatorInfo.colors ?? []);
        this.legendName.set(visualIndicatorInfo.indicatorLocation);
      });
  }

  protected verifyOpenPosition(event: MouseEvent | FocusEvent) {
    const dynamicallyPosition = this.dynamicallyPosition();
    if (!dynamicallyPosition) {
      this.showLegendComponent = true;
      return;
    }

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    // Percentage of the screen to open the dynamically position
    const screenPercentage = 1.2;

    const { clientX, clientY } = event as MouseEvent;
    const { bottom, right } = this.ringContainerPosition();

    const shouldDynamicallyPositionX = clientX > screenWidth / screenPercentage;
    const shouldDynamicallyPositionY = clientY > screenHeight / screenPercentage;

    this.dynamicallyPositionStyle = {
      ...this.ringContainerPosition(),
      right: shouldDynamicallyPositionX ? dynamicallyPosition?.right : right,
      bottom: shouldDynamicallyPositionY ? dynamicallyPosition?.bottom : bottom,
    };

    this.showLegendComponent = true;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
